/* What this deployment sends home, and who decided that.
 *
 * A baseline that never changes, and two things that can be added to it
 * independently.
 *
 * This was first written as three options that replaced each other, and the
 * first question it produced was whether choosing preview stopped the spectra.
 * Fair: a list of three alternatives says they are alternatives. They are not.
 * Spectra and the segment index arrive hourly whatever is set here, so the
 * baseline is now stated as a fact rather than offered as a choice.
 *
 * And the two audio options are not alternatives either. A proxy and the
 * originals compose: PRIORITY sends REDUCED before AUDIO, so the preview lands
 * in minutes and the archive of record catches up behind it. Wanting both is
 * the ordinary case -- listen at breakfast, keep the FLAC -- so these are
 * checkboxes rather than a radio group. A radio group would have meant
 * "exactly one", which is a rule nothing here needs.
 *
 * Each is a standing archive job; unticking cancels it. Driving "full quality"
 * from the mission tier instead would have been two ways to reach one state,
 * two places to look when it is wrong, and only one of them audited. The tier
 * is untouched, so live listening and clips keep working throughout.
 */
import { useState } from 'react'
import {
  ApiError, createArchiveJob, changeArchiveJob, fmtBytes, fmtTime,
  type ArchiveJob, type ArchiveNode,
} from '../api'

/** Measured on no-bgo-001: 510 MB/h of FLAC, and a proxy 17x smaller. */
const FULL_GB_PER_DAY = 510e6 * 24 / 1e9
const PREVIEW_GB_PER_DAY = 64_000 / 8 * 86400 / 1e9

interface Option {
  id: 'preview' | 'full'
  label: string
  gbPerDay: number
  blurb: string
}

const OPTIONS: Option[] = [
  {
    id: 'preview',
    label: 'Preview',
    gbPerDay: PREVIEW_GB_PER_DAY,
    blurb: '64 kbit AAC, about 17x smaller — an hour arrives in under three '
         + 'minutes instead of about fifty. The hydrophone is calibrated to '
         + '10 kHz and this carries well past that, so it discards nothing '
         + 'that could have been reported as a measurement. Still a proxy: '
         + 'the original stays on the card.',
  },
  {
    id: 'full',
    label: 'Full quality',
    gbPerDay: FULL_GB_PER_DAY,
    blurb: '24-bit FLAC, the archive of record. An hour of it is about 49 '
         + 'minutes over a 1.4 Mbit link, so this suits a site with real '
         + 'bandwidth or a deployment worth the wait.',
  },
]

/** The standing job behind an option, if there is one. */
function standingFor(jobs: ArchiveJob[], id: Option['id']): ArchiveJob | undefined {
  return jobs.find((j) =>
    j.state === 'active'
    && (j.mode === 'continuous' || j.mode === 'rolling')
    && (id === 'preview' ? j.format_kind === 'reduced' : j.format_kind === null))
}

/** Feature key per option. The receiver gates on these exact strings, so the
 *  screen and the check cannot drift into being about different things. */
const FEATURE: Record<string, string> = {
  preview: 'listening.archive_preview',
  full: 'listening.archive_full',
}

export function Delivery({
  missionId, jobs, node, canWrite, busy, onChanged, features,
}: {
  missionId: string
  jobs: ArchiveJob[]
  node: ArchiveNode | null
  canWrite: boolean
  /** What this farm has bought. Undefined means a receiver that does not
   *  report it, and everything is shown -- the old behaviour. */
  features?: string[]
  busy?: boolean
  onChanged: () => void
}) {
  const [saving, setSaving] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  /* An option the customer has not bought renders as ABSENT, not as a
     disabled row with an upsell. Two reasons, and the second is the binding
     one: a greyed-out control invites a support call about a thing they cannot
     have, and this bundle is served unauthenticated with sourcemaps, so "not
     included in your plan" would be published sales copy enumerating the
     catalogue to anyone who opens devtools.
  
     Undefined features means an older receiver that does not report them, and
     everything is shown -- the two deploy independently. */
  const allowed = features === undefined
    ? OPTIONS
    : OPTIONS.filter((o) => features.includes(FEATURE[o.id]!))
  const live = allowed.map((o) => ({ o, job: standingFor(jobs, o.id) }))
  const extra = live.reduce((a, { o, job }) => a + (job ? o.gbPerDay : 0), 0)

  const toggle = async (o: Option, on: boolean) => {
    setSaving(o.id)
    setErr(null)
    try {
      if (!on) {
        const job = standingFor(jobs, o.id)
        if (job) await changeArchiveJob(missionId, job.job_id, 'cancel')
      } else {
        await createArchiveJob(missionId, {
          mode: 'continuous',
          from_utc: new Date().toISOString(),
          acknowledge_cost: true,
          label: o.id === 'preview' ? 'Continuous preview' : 'Continuous full quality',
          ...(o.id === 'preview'
            ? { format_kind: 'reduced', format_codec: 'aac', format_bitrate_kbps: 64 }
            : {}),
        })
      }
      onChanged()
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail ?? e.code : 'Could not change this.')
      onChanged()
    } finally {
      setSaving(null)
    }
  }

  return (
    <div className="card">
      <h2 className="display" style={{ fontSize: 17, marginBottom: 4 }}>
        What this deployment sends home
      </h2>
      <p className="faint" style={{ fontSize: 12, margin: '0 0 12px' }}>
        The node picks a change up on its next check-in. Nothing here affects
        what is already stored.
      </p>

      {/* Stated, not offered. Turning these off is not a setting on this page:
          they are the scientific product, ~600x smaller than audio, and the
          reason there is anything to look at on the Sound tab at all. */}
      <div className="deliv baseline">
        <div className="row" style={{ gap: 10, alignItems: 'baseline' }}>
          <strong>Spectra and detections</strong>
          <span className="badge quiet num">~6 MB/day</span>
          <span className="badge on">always</span>
        </div>
        <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>
          Hourly levels, frequency bands and the segment index. These arrive
          whatever else is chosen below, and everything on the Sound tab is
          drawn from them.
        </div>
      </div>

      <p className="faint" style={{ fontSize: 12, margin: '14px 0 8px' }}>
        Sound, in addition — both may be on at once. The preview arrives first
        and the originals follow behind it.
      </p>

      {/* Said before the choices, not after. A node that cannot act on these
          makes them inert rather than slow: no proxy, no originals, no error,
          and nothing in a log on either side. An interface that reported "on
          since 21:08" over that would be describing something that has never
          once happened. */}
      {node && !node.archive_capable && (
        <div className="notice bad" style={{ marginBottom: 10 }}>
          <strong>This node cannot fetch sound yet.</strong> It is online and
          sending spectra, but it is running software from before the fetch
          path existed — it has never reported which configuration revision it
          is acting on. Anything switched on below will sit at nothing until
          the node is updated, without an error anywhere.
        </div>
      )}

      <div className="stack" style={{ gap: 8 }}>
        {live.map(({ o, job }) => {
          const on = !!job
          return (
            <label key={o.id} className={`deliv${on ? ' on' : ''}`}
              style={{ display: 'block', cursor: canWrite ? 'pointer' : 'default' }}>
              <div className="row" style={{ gap: 10, alignItems: 'baseline' }}>
                <input type="checkbox" checked={on} disabled={!canWrite || !!saving || busy}
                  onChange={(e) => void toggle(o, e.target.checked)} />
                <strong>{o.label}</strong>
                <span className="badge quiet num">
                  +{o.gbPerDay < 1 ? `${o.gbPerDay.toFixed(1)} GB` : `${Math.round(o.gbPerDay)} GB`}/day
                </span>
                {saving === o.id && <span className="faint">Saving…</span>}
                {on && job.segments_stored > 0 && (
                  <span className="faint num" style={{ marginLeft: 'auto' }}>
                    {fmtBytes(job.bytes_stored)} in {job.segments_stored}
                  </span>
                )}
              </div>
              <div className="faint" style={{ fontSize: 12, marginTop: 4, paddingLeft: 26 }}>
                {o.blurb}
              </div>
              {/* Per option, because they are turned on and off independently
                  and by different people. A setting that costs 12 GB a day
                  should never be anonymous. */}
              {on && (
                <div className="faint" style={{ fontSize: 12, marginTop: 6, paddingLeft: 26 }}>
                  On since {fmtTime(job.created_at)}, by{' '}
                  <strong>{job.requested_by_name ?? 'a token'}</strong>.
                </div>
              )}
            </label>
          )
        })}
      </div>

      {extra > 0 && (
        <p className="faint num" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
          Together: about {extra < 1 ? extra.toFixed(1) : Math.round(extra)} GB a
          day, {Math.round(extra * 30)} GB a month.
        </p>
      )}

      {err && <div className="notice bad" style={{ marginTop: 12 }}>{err}</div>}
    </div>
  )
}
